import { useEffect, useRef } from 'react';
import { formatCurrency } from '../utils/shared';
import TitleNumberCard from './atoms/TitleNumberCard';
import CustomBarChart from './BarChart';
import CustomPieChart from './PieChart';
import CustomLineChart from './LineChart';
import { useGenerateAiQueryMutation, useGetEventAssistanceReportQuery } from '../store';
import useGetParams from '../hooks/useGetParams';
import LoadingLottie from './atoms/LoadingLottie';
import { parseFriendlyDateAndHour } from '../utils/dates';
import AnimatedLoadingText from './atoms/AnimatedLoadingText';
import EventDTO from '../models/event';

const AssistanceReportTab = ({ event, hideAiSummary }: { event: EventDTO | null; hideAiSummary?: boolean }) => {
  const { eventId, businessId } = useGetParams();
  const { data, isLoading } = useGetEventAssistanceReportQuery({
    eventId,
    businessId,
  });

  // Only enable AI summary when not hidden
  const [generateAiQuery, { isLoading: isGeneratingAiQuery, data: aiQueryData }] = hideAiSummary
    ? [() => {}, { isLoading: false, data: null }]
    : useGenerateAiQueryMutation();
  const aiTriggeredForEventRef = useRef<string | null>(null);
  useEffect(() => {
    if (!data) {
      return;
    }
    if (aiTriggeredForEventRef.current === eventId) {
      return;
    }
    aiTriggeredForEventRef.current = eventId;
    const hasEventPassed = event?.date ? new Date() > new Date(event.date) : false;
    const dataWithEventDate: any = {
      // Shares and distributions
      sales_channel_share: data.sales_channel_share,
      free_tickets_share: data.free_tickets_share,
      sold_tickets_share: data.sold_tickets_share,

      // Totals and counts
      tickets_sold_count: data.tickets_sold_count,
      free_tickets_count: data.free_tickets_count,
      average_order_amount: data.average_order_amount,
      average_tickets_per_order: data.average_tickets_per_order,

      // Breakdowns
      total_tickets_taken_by_type: data.total_tickets_taken_by_type,
      total_tickets_taken_by_channel: data.total_tickets_taken_by_channel,

      // Time series
      sales_in_period: data.sales_in_period,
      sales_until_event_end: data.sales_until_event_end,
      sales_by_hour: data.sales_by_hour,

      // Event context
      event_date: event?.date,
      event_end_date: event?.ends_at,
      current_date: new Date().toISOString(),
    };

    if (hasEventPassed) {
      dataWithEventDate.asistance_share = data.asistance_share;
      dataWithEventDate.asistance_share_assited = data.asistance_share.assited;
      dataWithEventDate.asistance_share_not_assited = data.asistance_share.not_assited;
      dataWithEventDate.non_attendance_rate_by_ticket_names = data.non_attendance_rate_by_ticket.map((t) => t.ticket_name);
      dataWithEventDate.non_attendance_rate_by_ticket_rates = data.non_attendance_rate_by_ticket.map((t) => t.rate);
      dataWithEventDate.total_tickets_scanned = data.total_tickets_scanned;
      dataWithEventDate.tickets_used_by_hour = data.tickets_used_by_hour;
    }
    generateAiQuery({ contextJson: JSON.stringify(dataWithEventDate), businessId });
  }, [data, eventId, generateAiQuery, businessId]);

  if (isLoading) {
    return <LoadingLottie />;
  }

  if (!data) {
    return <div>No hay datos</div>;
  }
  const assistancePieChartData = [
    { name: 'Asistencia', value: data?.asistance_share.assited || 0 },
    { name: 'Inasistencia', value: data?.asistance_share.not_assited || 0 },
  ];

  const assistanceMediumPieChartData = [
    { name: 'Venta online', value: data?.sales_channel_share.online || 0 },
    { name: 'Venta presencial', value: data?.sales_channel_share.in_person || 0 },
    { name: 'Venta manual', value: data?.sales_channel_share.manual || 0 },
  ];

  const assistanceFreeTicketsPieChartData = [
    { name: 'Sin cargo', value: data?.free_tickets_share.free || 0 },
    { name: 'Vendidas', value: data?.free_tickets_share.not_free || 0 },
  ];

  const availableTicketsPieChartData = [
    { name: 'Vendidos', value: data?.sold_tickets_share.not_available || 0 },
    { name: 'No vendidos', value: data?.sold_tickets_share.available || 0 },
  ];

  const typeOfTicketBarChartData = data?.total_tickets_taken_by_type.map((t) => ({
    name: t.ticket_name,
    pv: t.amount_taken,
  })) || [];

  const ticketsTakenByChannelBarChartData = [
    { name: 'Avantti', pv: data?.total_tickets_taken_by_channel.avantti || 0 },
    { name: 'Referidos', pv: data?.total_tickets_taken_by_channel.referrals || 0 },
    { name: 'Vendedores', pv: data?.total_tickets_taken_by_channel.seller || 0 },
  ];

  const nonAttendanceRateByTicketBarChartData = data?.non_attendance_rate_by_ticket.map((t) => ({
    name: t.ticket_name,
    pv: t.rate || 0,
  })) || [];

  const salesInPeriodLineChartData = data?.sales_in_period
    ? Object.entries(data.sales_in_period).map(([date, amount]) => ({
      name: parseFriendlyDateAndHour(date, false, true),
      pv: amount as number,
      amt: amount as number,
    }))
    : [];

  const salesUntilEventEndLineChartData = data?.sales_until_event_end
    ? Object.entries(data.sales_until_event_end).map(([date, amount]) => ({
      name: parseFriendlyDateAndHour(date, false, true),
      pv: amount as number,
      amt: amount as number,
    }))
    : [];

  const salesByHourLineChartData = data?.sales_by_hour
    ? Object.entries(data.sales_by_hour).map(([hour, amount]) => ({
      name: `${hour}:00 hs`,
      pv: amount as number,
      amt: amount as number,
    }))
    : [];

  const ticketsUsedByHourLineChartData = data?.tickets_used_by_hour
    ? Object.entries(data.tickets_used_by_hour)
      .map(([hour, amount]) => ({
        name: `${hour}:00 hs`,
        pv: amount as number,
        amt: amount as number,
        hour: parseInt(hour, 10),
      }))
      .sort((a, b) => {
        // Late PM hours (20-23) come first, then AM hours (0-19)
        const aSort = a.hour >= 20 ? a.hour - 24 : a.hour + 100;
        const bSort = b.hour >= 20 ? b.hour - 24 : b.hour + 100;
        return aSort - bSort;
      })
      .map(({ hour, ...rest }) => rest)
    : [];

  return (
    <div className='flex flex-col gap-4'>
      {!hideAiSummary && (
        <AnimatedLoadingText isLoading={isGeneratingAiQuery} loadingText="Generando reporte con IA..." text={aiQueryData?.data?.output_text} title='Resumen IA (BETA)' />
      )}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full glass-surface'>
        <div className='w-full'>
          <TitleNumberCard
            title="Valor promedio de compra"
            number={formatCurrency(data?.average_order_amount || 0)}
          />
        </div>
        <div className='w-full'>
          <TitleNumberCard
            title="Entradas promedio por compra"
            number={data?.average_tickets_per_order || 0}
          />
        </div>
        <div className='w-full'>
          <TitleNumberCard
            title="Total entradas"
            number={data?.tickets_count || 0}
          />
        </div>
        <div className='w-full'>
          <TitleNumberCard
            title="Entradas vendidas (precio mayor a $0)"
            number={data?.tickets_sold_count || 0}
          />
        </div>
        <div className='w-full'>
          <TitleNumberCard
            title="Cantidad de tickets escaneados"
            number={data?.total_tickets_scanned || 0}
          />
        </div>
        <div className='w-full'>
          <TitleNumberCard
            title="Cantidad de entradas sin cargo"
            number={data?.free_tickets_count || 0}
          />
        </div>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-8 w-full glass-surface'>
        <div className='w-full glass-surface'>
          <h1 className='text-center'>Porcentaje de asistencia</h1>
          <CustomPieChart data={assistancePieChartData} />
        </div>
        <div className='w-full glass-surface'>
          <h1 className='text-center'>Porcentaje de tipo de venta</h1>
          <CustomPieChart data={assistanceMediumPieChartData} />
        </div>
        <div className='w-full glass-surface'>
          <h1 className='text-center'>Porcentaje de entradas sin cargo sobre el total</h1>
          <CustomPieChart data={assistanceFreeTicketsPieChartData} />
        </div>
        <div className='w-full glass-surface'>
          <h1 className='text-center'>Porcentaje de tickets vendidos respecto al total disponible</h1>
          <CustomPieChart data={availableTicketsPieChartData} />
        </div>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-8 w-full glass-surface'>
        <div className='w-full glass-surface'>
          <h1 className='text-center'>Total de entradas vendidas por tipo</h1>
          <CustomBarChart data={typeOfTicketBarChartData} />
        </div>
        <div className='w-full glass-surface'>
          <h1 className='text-center'>Cantidad de ventas por canal</h1>
          <CustomBarChart data={ticketsTakenByChannelBarChartData} />
        </div>
        <div className='w-full glass-surface'>
          <h1 className='text-center'>Porcentaje de inasistencia por tipo de orden</h1>
          <CustomBarChart data={nonAttendanceRateByTicketBarChartData} />
        </div>
      </div>
      <div className='flex flex-col gap-8 w-full glass-surface'>
        <div className='w-full glass-surface'>
          <h1 className='text-center'>Monto vendido a lo largo del tiempo</h1>
          <CustomLineChart data={salesInPeriodLineChartData} />
        </div>
        <div className='w-full glass-surface'>
          <h1 className='text-center'>Cantidad de ventas hasta el fin del evento</h1>
          <CustomLineChart data={salesUntilEventEndLineChartData} />
        </div>
        <div className='w-full glass-surface'>
          <h1 className='text-center'>Horarios de venta</h1>
          <CustomLineChart data={salesByHourLineChartData} />
        </div>
        {ticketsUsedByHourLineChartData.length > 0 && <div className='w-full glass-surface'>
          <h1 className='text-center'>Distribución hora de asistencia</h1>
          <CustomLineChart data={ticketsUsedByHourLineChartData} />
        </div>}
      </div>
    </div>
  );
};

export default AssistanceReportTab;
